import React, { useState, useEffect, useContext } from 'react';
import { GlobalContext } from '../context/GlobalContext';
import messengerApi from '../lib/messengerApi';
import usersApi from '../lib/usersApi';
import { successToast, errorToast } from '../lib/helpers';
import { HashtagIcon, EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import ChannelList from '../components/Messenger/ChannelList';
import ChannelMessageArea from '../components/Messenger/ChannelMessageArea';
import ChannelInput from '../components/Messenger/ChannelInput';
import CreateChannelModal from '../components/Messenger/CreateChannelModal';
import ChannelSettingsModal from '../components/Messenger/ChannelSettingsModal';

const Messenger = () => {
  const { uinfo } = useContext(GlobalContext);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [messageInput, setMessageInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [channels, setChannels] = useState([]);
  const [messages, setMessages] = useState({});
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [userId, setUserId] = useState(null);
  const [channelMembers, setChannelMembers] = useState({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  // Get user_id from users table based on uinfo
  useEffect(() => {
    const fetchUserId = async () => {
      if (!uinfo) return;
      
      try {
        // Try to find user by username or email
        const username = uinfo.Username || uinfo.username;
        const email = uinfo.Email || uinfo.email;
        
        console.log('[Messenger] Looking up user with uinfo:', uinfo);
        console.log('[Messenger] Username:', username, 'Email:', email);
        
        if (username || email) {
          // First try exact username match
          let response = await usersApi.get('/api/users', {
            params: {
              search: username || email
            }
          });
          
          if (response.success && response.data && response.data.length > 0) {
            // Find exact match - try username first, then email
            let user = response.data.find(u => 
              u.username?.toLowerCase() === username?.toLowerCase()
            );
            
            // If not found, try email
            if (!user) {
              user = response.data.find(u => 
                u.email?.toLowerCase() === email?.toLowerCase()
              );
            }
            
            // If still not found, use first result
            if (!user && response.data.length > 0) {
              user = response.data[0];
            }
            
            if (user) {
              console.log('[Messenger] Found user:', user);
              console.log('[Messenger] Setting userId to:', user.user_id, 'type:', typeof user.user_id);
              setUserId(user.user_id);
            } else {
              console.warn('[Messenger] User object not found in response');
            }
          } else {
            console.warn('[Messenger] No user found for username:', username, 'email:', email);
            console.warn('[Messenger] Response:', response);
          }
        }
      } catch (error) {
        console.error('Error fetching user_id:', error);
        // Continue without user_id - will show error when trying to use messenger
      }
    };
    
    fetchUserId();
  }, [uinfo]);

  // Fetch channels
  useEffect(() => {
    const fetchChannels = async () => {
      if (!userId) return;
      
      try {
        setLoading(true);
        const response = await messengerApi.get('/api/messenger/channels', {
          params: { user_id: userId }
        });
        
        if (response.success) {
          setChannels(response.data || []);
          
          // Set first channel as selected if none selected
          if (!selectedChannel && response.data && response.data.length > 0) {
            setSelectedChannel(response.data[0]);
          }
        }
      } catch (error) {
        console.error('Error fetching channels:', error);
        errorToast('Failed to load channels');
      } finally {
        setLoading(false);
      }
    };
    
    fetchChannels();
  }, [userId]);

  // Fetch channel members function (extracted so it can be called from callbacks)
  const fetchChannelMembers = async (channelId) => {
    if (!channelId || !userId) return;
    
    try {
      const response = await messengerApi.get(`/api/messenger/channels/${channelId}/members`, {
        params: { user_id: userId }
      });
      
      if (response.success) {
        setChannelMembers(prev => ({
          ...prev,
          [channelId]: response.data || []
        }));
      }
    } catch (error) {
      console.error('Error fetching members:', error);
    }
  };

  // Fetch messages for selected channel
  useEffect(() => {
    const fetchMessages = async () => {
      if (!selectedChannel || !userId) return;
      
      try {
        const response = await messengerApi.get(`/api/messenger/channels/${selectedChannel.id}/messages`, {
          params: { user_id: userId }
        });
        
        if (response.success) {
          setMessages(prev => ({
            ...prev,
            [selectedChannel.id]: response.data || []
          }));
        }
      } catch (error) {
        console.error('Error fetching messages:', error);
        errorToast('Failed to load messages');
      }
    };
    
    fetchMessages();
    
    // Fetch channel members
    if (selectedChannel) {
      fetchChannelMembers(selectedChannel.id);
    }
  }, [selectedChannel, userId]);

  const handleChannelCreated = async (newChannel) => {
    setChannels(prev => [newChannel, ...prev]);
    setSelectedChannel(newChannel);
    // Immediately fetch members for the new channel so admin status is recognized
    if (newChannel.id && userId) {
      await fetchChannelMembers(newChannel.id);
    }
  };

  const handleChannelUpdated = (updatedChannel) => {
    setChannels(prev => prev.map(ch => ch.id === updatedChannel.id ? updatedChannel : ch));
    setSelectedChannel(prev => prev?.id === updatedChannel.id ? updatedChannel : prev);
  };

  const handleChannelDeleted = (channelId) => {
    setChannels(prev => prev.filter(ch => ch.id !== channelId));
    if (selectedChannel?.id === channelId) {
      setSelectedChannel(null);
    }
  };

  const handleSendMessage = async () => {
    if (!messageInput.trim() || !selectedChannel || !userId || sending) return;

    try {
      setSending(true);
      const response = await messengerApi.post(
        `/api/messenger/channels/${selectedChannel.id}/messages`,
        {
          message: messageInput,
          user_id: userId
        }
      );
      
      if (response.success) {
        // Add new message to messages state
        setMessages(prev => ({
          ...prev,
          [selectedChannel.id]: [...(prev[selectedChannel.id] || []), response.data]
        }));
        
        setMessageInput('');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      errorToast('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const currentMessages = selectedChannel ? (messages[selectedChannel.id] || []) : [];

  // Show error if user_id not found
  if (!userId && uinfo) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" style={{ paddingTop: '4rem' }}>
        <div className="text-center">
          <p className="text-gray-600 mb-2">Unable to load messenger</p>
          <p className="text-sm text-gray-500">User account not found in system. Please contact an administrator.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 flex flex-col" style={{ height: 'calc(100vh - 4rem - 5px)', paddingTop: '4rem' }}>
      {/* Main Content Area - Chat Window */}
      <div className="flex-1 flex overflow-hidden px-4 py-2 min-h-0">
        <div className="flex gap-4 w-full h-full">
        {/* Left Sidebar - Channel List */}
        <ChannelList
          channels={channels}
          selectedChannel={selectedChannel}
          onChannelSelect={setSelectedChannel}
          onCreateChannel={() => setShowCreateModal(true)}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          loading={loading}
          userInfo={uinfo}
        />

        {/* Center - Message Area */}
        <div className="flex-1 flex flex-col bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
          {selectedChannel ? (
            <>
              {/* Channel Header */}
              <div className="px-6 py-2.5 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <HashtagIcon className="w-5 h-5 text-gray-400" />
                  <div>
                    <h2 className="text-base font-semibold text-gray-900">{selectedChannel.name}</h2>
                    <p className="text-xs text-gray-500">{selectedChannel.description}</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowSettingsModal(true)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                  title="Channel settings"
                >
                  <EllipsisVerticalIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Messages Area */}
              <ChannelMessageArea
                messages={currentMessages}
                currentUserId={userId}
                loading={loading && currentMessages.length === 0}
              />

              {/* Message Input */}
              <ChannelInput
                value={messageInput}
                onChange={setMessageInput}
                onSend={handleSendMessage}
                sending={sending}
                channelName={selectedChannel.name}
              />
            </>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <HashtagIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Select a channel</h3>
                <p className="text-gray-500">Choose a channel from the sidebar to start messaging</p>
              </div>
            </div>
          )}
        </div>

          {/* Right Sidebar - Channel Info (Optional, can be collapsed) */}
          {selectedChannel && (
            <div className="w-64 bg-white border border-gray-200 rounded-lg shadow-sm p-6 overflow-y-auto">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Channel Details</h3>
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">About</label>
                  <p className="text-sm text-gray-700 mt-1">{selectedChannel.description}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2 block">Members</label>
                  <div className="space-y-2">
                    {channelMembers[selectedChannel.id] && channelMembers[selectedChannel.id].length > 0 ? (
                      channelMembers[selectedChannel.id].map((member) => {
                        const name = member.display_name || member.username || member.email || 'Unknown';
                        const initials = name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';
                        const actualName = member.username || member.email || 'Unknown';
                        return (
                          <div key={member.user_id} className="flex items-center space-x-2">
                            <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center text-white font-semibold text-xs">
                              {initials}
                            </div>
                            <div className="flex-1">
                              <div className="text-sm text-gray-700">{name}</div>
                              {member.display_name && (
                                <div className="text-xs text-gray-500">{actualName}</div>
                              )}
                              {member.role === 'admin' && (
                                <span className="text-xs text-gray-500">(Admin)</span>
                              )}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-sm text-gray-500">Loading members...</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Channel Modal */}
      <CreateChannelModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onChannelCreated={handleChannelCreated}
        userId={userId}
      />

      {/* Channel Settings Modal */}
      {selectedChannel && (
        <ChannelSettingsModal
          isOpen={showSettingsModal}
          onClose={() => setShowSettingsModal(false)}
          channel={selectedChannel}
          userId={userId}
          onChannelUpdated={handleChannelUpdated}
          onChannelDeleted={handleChannelDeleted}
          onMembersUpdated={() => fetchChannelMembers(selectedChannel.id)}
        />
      )}
    </div>
  );
};

export default Messenger;

